import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { MarketingFooter } from '@/components/layout/MarketingFooter';
import { Button } from '@/components/ui/button';
import { SUPPORT_EMAIL } from '@/config/brandConfig';
import { useLanguage } from '@/i18n/LanguageContext';
import { trackEvent } from '@/lib/analytics';
import WorldCupPopup from '@/components/WorldCupPopup';
import {
  ArrowRight, BarChart3, Eye, Target, Shield, Lock,
  Layers, CheckCircle2, LineChart, FileText,
} from 'lucide-react';

const MR = { fontFamily: 'Manrope, sans-serif' };
const JB = { fontFamily: "'JetBrains Mono', monospace" };

const SOL_ICONS = [BarChart3, Eye, Target];
const DEL_ICONS = [LineChart, BarChart3, Target, FileText];
const TRUST_ICONS = [BarChart3, Layers, Lock, CheckCircle2];

export default function HomePage() {
  const { lang } = useLanguage();
  useEffect(() => { trackEvent('landing_view', { landing_type: 'home', language: lang }); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingHeader />
      <main>
        <Hero />
        <Problem />
        <Solution />
        <Deliverables />
        <Trust />
        <FinalCTA />
      </main>
      <MarketingFooter />
      <WorldCupPopup />
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   HERO
   ════════════════════════════════════════════════════════ */
function Hero() {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden pt-32 pb-14 md:pt-40 md:pb-20 lg:pt-48 lg:pb-24" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full bg-primary/[0.04] blur-[120px] translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-[hsl(150,50%,45%)]/[0.03] blur-[100px] -translate-x-1/4 translate-y-1/4" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/60 backdrop-blur-sm px-4 py-1.5" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(150,50%,45%)] animate-pulse" />
              <span className="text-xs font-medium text-muted-foreground tracking-wide" style={MR}>{t.hero.badge}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight" style={MR} data-testid="hero-heading">
              {t.hero.headline}
              <span className="text-primary">{t.hero.headlineAccent}</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2" data-testid="hero-ctas">
              <Link to="/briefing">
                <Button size="lg" className="bg-primary hover:bg-primary/90 font-semibold h-12 px-7 text-[15px] rounded-lg" data-testid="cta-briefing-hero">
                  {t.hero.ctaPrimary}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/club-demo">
                <Button variant="outline" size="lg" className="h-12 px-7 text-[15px] rounded-lg border-border/60 text-muted-foreground hover:text-foreground" data-testid="cta-demo-hero">
                  {t.hero.ctaSecondary}
                </Button>
              </Link>
            </div>

            <Link to="/login" className="inline-block text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid="cta-login-hero">
              {t.hero.signInPrompt} &rarr;
            </Link>
          </div>

          <div className="lg:col-span-5 hidden lg:flex items-center justify-center" aria-hidden="true">
            <HeroVisualization />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisualization() {
  return (
    <div className="relative w-[380px] h-[380px]" role="img" aria-label="Brand intelligence radar visualization showing five key brand dimensions">
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full animate-[spin_60s_linear_infinite]" aria-hidden="true">
        <circle cx="200" cy="200" r="180" fill="none" stroke="hsl(215,55%,52%)" strokeWidth="0.5" opacity="0.15" />
        <circle cx="200" cy="200" r="130" fill="none" stroke="hsl(215,55%,52%)" strokeWidth="0.5" opacity="0.1" />
        <circle cx="200" cy="200" r="80" fill="none" stroke="hsl(215,55%,52%)" strokeWidth="0.5" opacity="0.08" />
        {[0, 60, 120, 180, 240, 300].map((deg) => (
          <line key={deg} x1="200" y1="200" x2={200 + 180 * Math.cos((deg * Math.PI) / 180)} y2={200 + 180 * Math.sin((deg * Math.PI) / 180)} stroke="hsl(215,55%,52%)" strokeWidth="0.4" opacity="0.08" />
        ))}
      </svg>
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full" aria-hidden="true">
        <polygon points="200,50 340,140 320,300 140,320 80,170" fill="hsl(215,55%,52%)" fillOpacity="0.06" stroke="hsl(215,55%,52%)" strokeWidth="1" strokeOpacity="0.25" />
        {[[200, 50], [340, 140], [320, 300], [140, 320], [80, 170]].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="4" fill="hsl(215,55%,52%)" opacity="0.6">
            <animate attributeName="r" values="3;5;3" dur={`${2 + i * 0.4}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <circle cx="200" cy="200" r="6" fill="hsl(215,55%,52%)" opacity="0.4">
          <animate attributeName="r" values="4;8;4" dur="3s" repeatCount="indefinite" />
        </circle>
      </svg>
      <div className="absolute top-[8%] left-1/2 -translate-x-1/2 text-[10px] font-medium text-primary/50 tracking-widest uppercase" style={JB}>Value</div>
      <div className="absolute top-[30%] right-[2%] text-[10px] font-medium text-primary/40 tracking-widest uppercase" style={JB}>Digital</div>
      <div className="absolute bottom-[18%] right-[12%] text-[10px] font-medium text-primary/40 tracking-widest uppercase" style={JB}>Risk</div>
      <div className="absolute bottom-[14%] left-[10%] text-[10px] font-medium text-primary/40 tracking-widest uppercase" style={JB}>Commercial</div>
      <div className="absolute top-[36%] left-[2%] text-[10px] font-medium text-primary/40 tracking-widest uppercase" style={JB}>Trajectory</div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PROBLEM
   ════════════════════════════════════════════════════════ */
function Problem() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-24 px-4 md:px-8 border-t border-border/30" data-testid="section-problem">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-5" style={MR}>{t.problem.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-8" style={MR}>
            {t.problem.headline}
            <span className="text-muted-foreground">{t.problem.headlineAccent}</span>
          </h2>
          <ul className="space-y-4">
            {t.problem.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[hsl(38,85%,55%)] mt-2 flex-shrink-0" />
                <span className="text-base text-muted-foreground leading-relaxed">{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   SOLUTION — 3 Pillars
   ════════════════════════════════════════════════════════ */
function Solution() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-24 px-4 md:px-8 bg-card/20 border-y border-border/30" data-testid="section-solution">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-5" style={MR}>{t.solution.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={MR}>
            {t.solution.headline}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {t.solution.cards.map((card, i) => {
            const Icon = SOL_ICONS[i];
            return (
              <div key={card.title} className="rounded-xl border border-border/50 bg-card/60 p-8 transition-all duration-300 hover:border-primary/25 hover:shadow-[0_0_40px_-12px_hsl(215,55%,52%,0.12)]" data-testid={`solution-${card.title.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground" style={MR}>{card.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/club-demo">
            <Button variant="outline" className="rounded-lg border-border/60 text-[13px] h-10 px-6" data-testid="cta-solution-demo">
              {t.solution.cta}
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   DELIVERABLES
   ════════════════════════════════════════════════════════ */
function Deliverables() {
  const { t } = useLanguage();
  return (
    <section className="py-16 md:py-24 px-4 md:px-8" data-testid="section-deliverables">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary mb-5" style={MR}>{t.deliverables.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={MR}>
            {t.deliverables.headline}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {t.deliverables.items.map((item, i) => {
            const Icon = DEL_ICONS[i];
            return (
              <div key={item.title} className="flex gap-4 p-6 rounded-xl border border-border/40 bg-card/40 transition-all duration-200 hover:border-border/60 hover:bg-card/60" data-testid={`deliverable-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-primary/8 border border-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4.5 h-4.5 text-primary/70" />
                </div>
                <div>
                  <h4 className="text-[15px] font-semibold text-foreground mb-1.5" style={MR}>{item.title}</h4>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/briefing">
            <Button className="bg-primary hover:bg-primary/90 font-semibold h-11 px-7 text-[14px] rounded-lg" data-testid="cta-deliverables-briefing">
              {t.deliverables.cta}
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   TRUST / CREDIBILITY
   ════════════════════════════════════════════════════════ */
function Trust() {
  const { t } = useLanguage();
  return (
    <section className="py-20 md:py-28 px-4 md:px-8 bg-card/20 border-y border-border/30" data-testid="section-trust">
      <div className="max-w-[1400px] mx-auto">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[hsl(150,50%,45%)] mb-5" style={MR}>{t.trust.eyebrow}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" style={MR}>
            {t.trust.headline}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {t.trust.items.map((item, i) => {
            const Icon = TRUST_ICONS[i];
            return (
              <div key={item.title} className="flex gap-3.5 p-5 rounded-lg border border-border/30 bg-card/30" data-testid={`trust-${i}`}>
                <div className="w-9 h-9 rounded-md bg-[hsl(150,50%,45%)]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[hsl(150,50%,45%)]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   FINAL CTA
   ════════════════════════════════════════════════════════ */
function FinalCTA() {
  const { t } = useLanguage();
  return (
    <section className="py-24 md:py-28 px-4 md:px-8 relative overflow-hidden" data-testid="section-final-cta">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.03] blur-[100px]" />
      </div>

      <div className="max-w-3xl mx-auto text-center relative">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-6" style={MR}>
          {t.finalCta.headline}
          <span className="text-primary">{t.finalCta.headlineAccent}</span>
        </h2>
        <p className="text-muted-foreground text-base md:text-lg mb-12 max-w-lg mx-auto leading-relaxed">
          {t.finalCta.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="final-ctas">
          <Link to="/briefing">
            <Button size="lg" className="bg-primary hover:bg-primary/90 font-semibold h-13 px-8 text-[15px] rounded-lg" data-testid="cta-briefing-final">
              {t.finalCta.ctaPrimary}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/club-demo">
            <Button variant="outline" size="lg" className="h-13 px-8 text-[15px] rounded-lg border-border/60 text-muted-foreground hover:text-foreground" data-testid="cta-demo-final">
              {t.finalCta.ctaSecondary}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════
   FOOTER
   ════════════════════════════════════════════════════════ */
function Footer() {
  const { t } = useLanguage();
  return (
    <footer className="border-t border-border/40 py-10 md:py-12">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-8 mb-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center">
                <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-label="YBV logo"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              </div>
              <span className="text-sm font-semibold text-foreground" style={MR}>YBV</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h4 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-[0.15em] mb-4" style={MR}>{t.footer.platformTitle}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/briefing" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.requestBriefing}</Link></li>
              <li><Link to="/club-demo" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.clubDemo}</Link></li>
              <li><Link to="/world-cup-brand-index" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-wcbi-link">{t.footer.wcbi}</Link></li>
              <li><Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.signIn}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-[0.15em] mb-4" style={MR}>{t.footer.legalTitle}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.privacy}</Link></li>
              <li><Link to="/terms-and-conditions" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.terms}</Link></li>
              <li><Link to="/legal-notice" className="text-muted-foreground hover:text-foreground transition-colors">{t.footer.legalNotice}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-[0.15em] mb-4" style={MR}>{t.footer.contactTitle}</h4>
            <p className="text-sm text-muted-foreground">{SUPPORT_EMAIL}</p>
          </div>
        </div>

        <div className="pt-8 border-t border-border/30">
          <p className="text-[10px] text-muted-foreground/35 max-w-3xl leading-relaxed">
            {t.footer.disclaimer}
          </p>
          <p className="text-[11px] text-muted-foreground/30 mt-3">
            &copy; {new Date().getFullYear()} {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
}
